/**
 * Componente para capturar la foto de verificación del padre/acudiente.
 *
 * Estrategia (verificación obligatoria — no se permite cargar desde galería):
 *  1. Intenta `getUserMedia` con cámara en vivo (front o trasera).
 *  2. Si el navegador no expone `getUserMedia` (entornos sin cámara), usa fallback
 *     de `<input type="file" accept="image/*" capture>` que en móvil abre la cámara
 *     nativa directamente sin permitir elegir desde galería.
 *  3. Devuelve un File listo para upload.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, RefreshCw, SwitchCamera, X } from "lucide-react";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/utils";

export interface ParentPhotoCaptureProps {
  label?: string;
  hint?: string;
  disabled?: boolean;
  onCapture: (file: File, previewUrl: string) => void;
  onClear?: () => void;
  initialPreviewUrl?: string;
  className?: string;
  /**
   * Cámara preferida cuando el usuario inicia la captura.
   *  - "user" (default): selfie
   *  - "environment": trasera (cuando el director toma la foto a un tercero)
   */
  defaultFacingMode?: "user" | "environment";
}

export function ParentPhotoCapture({
  label = "Foto de verificación del padre/acudiente",
  hint = "Tome una foto del padre/madre/acudiente firmante. Esta foto se usa para verificar la veracidad de la firma.",
  disabled,
  onCapture,
  onClear,
  initialPreviewUrl,
  className,
  defaultFacingMode = "user",
}: ParentPhotoCaptureProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  // URL de objeto creada por nosotros (para revocarla en cleanup)
  const ownedPreviewUrlRef = useRef<string | null>(null);

  const [mode, setMode] = useState<"idle" | "camera" | "captured">(
    initialPreviewUrl ? "captured" : "idle",
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    initialPreviewUrl ?? null,
  );
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    defaultFacingMode,
  );
  // Stream activo en estado (no ref) para que el useEffect lo detecte y adjunte
  // al <video> de forma reactiva. Esto resuelve race conditions en Chrome móvil
  // al hacer flip (la cámara anterior se libera correctamente antes de pintar).
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Si el navegador no soporta getUserMedia (raro en móviles modernos), permitimos
  // como fallback el input nativo con `capture` — que en iOS/Android abre la cámara
  // del sistema directamente, no la galería.
  const supportsGetUserMedia =
    typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
  // En móviles, el atributo HTML `capture` fuerza la cámara nativa (no galería).
  // Lo usamos como fallback de seguridad si getUserMedia falla por permisos.
  const isMobile =
    typeof navigator !== "undefined" &&
    (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
      (typeof window !== "undefined" &&
        "ontouchstart" in window &&
        navigator.maxTouchPoints > 0));

  const stopStream = useCallback((s: MediaStream | null) => {
    if (!s) return;
    s.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
  }, []);

  const stopCamera = useCallback(() => {
    setStream((prev) => {
      stopStream(prev);
      return null;
    });
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {
        /* ignore */
      }
      videoRef.current.srcObject = null;
    }
  }, [stopStream]);

  // Revoca la URL del preview previo cuando ya no se usa.
  const setOwnedPreview = useCallback((url: string | null) => {
    if (ownedPreviewUrlRef.current && ownedPreviewUrlRef.current !== url) {
      URL.revokeObjectURL(ownedPreviewUrlRef.current);
    }
    ownedPreviewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
      if (ownedPreviewUrlRef.current) {
        URL.revokeObjectURL(ownedPreviewUrlRef.current);
        ownedPreviewUrlRef.current = null;
      }
    };
  }, [stopCamera]);

  // Adjunta el stream activo al <video> cuando estamos en modo cámara y el
  // elemento ya está montado en el DOM. Reactivo al cambio de stream para
  // soportar correctamente el flip en Chrome móvil sin pantalla negra.
  useEffect(() => {
    if (mode !== "camera" || !stream) return;
    const video = videoRef.current;
    if (!video) return;
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.muted = true;
    // Si había un stream previo, hay que limpiar primero para que Chrome móvil
    // suelte el frame anterior antes de pintar el nuevo (evita pantalla negra).
    if (video.srcObject && video.srcObject !== stream) {
      try {
        video.pause();
      } catch {
        /* ignore */
      }
      video.srcObject = null;
      video.load();
    }
    video.srcObject = stream;
    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch((err) => {
          console.warn("[ParentPhotoCapture] video.play() failed", err);
        });
      }
    };
    if (video.readyState >= 2) {
      tryPlay();
    } else {
      video.addEventListener("loadedmetadata", tryPlay, { once: true });
      return () => video.removeEventListener("loadedmetadata", tryPlay);
    }
  }, [mode, stream]);

  const startCamera = useCallback(
    async (mode?: "user" | "environment") => {
      const target = mode ?? facingMode;
      setError(null);
      // Detener stream anterior y esperar un microtask para que Chrome móvil
      // libere efectivamente la cámara antes de pedir la otra orientación.
      setStream((prev) => {
        stopStream(prev);
        return null;
      });
      if (videoRef.current) {
        try {
          videoRef.current.pause();
        } catch {
          /* ignore */
        }
        videoRef.current.srcObject = null;
      }
      // pequeño delay para Chrome Android: evita InvalidStateError al pedir
      // la otra cámara antes de que el sensor previo se haya liberado.
      await new Promise((r) => setTimeout(r, 80));
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: target },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        setStream(newStream);
        setMode("camera");
      } catch (err) {
        console.warn("[ParentPhotoCapture] camera error", err);
        const e = err as { name?: string };
        const msg =
          e?.name === "NotAllowedError"
            ? "Permiso de cámara denegado. Habilita el permiso en el navegador (ícono de candado de la barra de direcciones) y vuelve a intentar."
            : e?.name === "NotFoundError" || e?.name === "OverconstrainedError"
              ? "No se encontró una cámara con esa orientación. Intenta con la otra cámara o usa otro dispositivo."
              : "No se pudo acceder a la cámara. Verifica los permisos y vuelve a intentar. Por seguridad, esta foto debe tomarse en el momento.";
        setError(msg);
        setMode("idle");
      }
    },
    [facingMode, stopStream],
  );

  const handleFlipCamera = useCallback(async () => {
    const next = facingMode === "user" ? "environment" : "user";
    setFacingMode(next);
    await startCamera(next);
  }, [facingMode, startCamera]);

  const captureFromVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], `parent-photo-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        const url = URL.createObjectURL(blob);
        setOwnedPreview(url);
        setMode("captured");
        stopCamera();
        onCapture(file, url);
      },
      "image/jpeg",
      0.9,
    );
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("El archivo debe ser una imagen tomada con la cámara");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError("La imagen no puede superar los 8MB");
      return;
    }
    // Materializar en memoria de inmediato: el File del selector se puede
    // invalidar antes de subirse al confirmar el consentimiento (NotReadableError
    // en móvil). Leer los bytes ahora deja una copia estable.
    let safeFile = file;
    try {
      const buffer = await file.arrayBuffer();
      safeFile = new File([buffer], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
    } catch (err) {
      console.error("No se pudo leer la imagen seleccionada:", err);
      setError("No se pudo leer la imagen. Vuelve a tomarla.");
      return;
    }
    const url = URL.createObjectURL(safeFile);
    setOwnedPreview(url);
    setMode("captured");
    setError(null);
    onCapture(safeFile, url);
  };

  const handleRetake = () => {
    setOwnedPreview(null);
    setMode("idle");
    onClear?.();
  };

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      {hint && <p className="text-xs text-gray-500 mb-2">{hint}</p>}

      <div className="border-2 border-gray-300 rounded-lg p-3 bg-gray-50">
        {mode === "idle" && (
          <div className="flex flex-col items-center gap-3 py-5 sm:py-6">
            <Camera className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
            <p className="text-[11px] text-center text-gray-600 px-2 max-w-xs">
              Por seguridad, esta foto debe tomarse en el momento con la cámara.
              No se permite cargar imágenes desde la galería.
            </p>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                if (supportsGetUserMedia) {
                  void startCamera();
                } else {
                  fileInputRef.current?.click();
                }
              }}
              disabled={disabled}
            >
              <Camera className="w-4 h-4 mr-1 inline" />
              Tomar foto con cámara
            </Button>
            {/* Fallback nativo (sin permitir galería: capture fuerza cámara). */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture={defaultFacingMode}
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        )}

        {mode === "camera" && (
          <div className="flex flex-col items-center gap-2">
            <div className="relative w-full max-w-sm">
              <video
                ref={videoRef}
                className="w-full rounded-lg bg-black"
                playsInline
                muted
                autoPlay
              />
              <button
                type="button"
                onClick={handleFlipCamera}
                title="Cambiar cámara"
                className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/55 text-white flex items-center justify-center backdrop-blur-sm hover:bg-black/70"
              >
                <SwitchCamera className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={captureFromVideo}
              >
                <Camera className="w-4 h-4 mr-1 inline" />
                Capturar
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  stopCamera();
                  setMode("idle");
                }}
              >
                <X className="w-4 h-4 mr-1 inline" />
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {mode === "captured" && previewUrl && (
          <div className="flex flex-col items-center gap-2">
            <img
              src={previewUrl}
              alt="Foto del padre"
              className="max-h-64 rounded-lg"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRetake}
              disabled={disabled}
            >
              <RefreshCw className="w-4 h-4 mr-1 inline" />
              Tomar otra foto
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 p-2 rounded bg-red-50 border border-red-200 text-red-700 space-y-2">
          <p className="text-xs leading-relaxed">{error}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="xs"
              onClick={() => {
                setError(null);
                void startCamera();
              }}
              disabled={disabled}
            >
              <RefreshCw className="w-3 h-3 mr-1 inline" />
              Reintentar
            </Button>
            {isMobile && (
              <Button
                type="button"
                variant="outline"
                size="xs"
                onClick={() => {
                  setError(null);
                  fileInputRef.current?.click();
                }}
                disabled={disabled}
              >
                <Camera className="w-3 h-3 mr-1 inline" />
                Usar cámara del teléfono
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentPhotoCapture;
